import '../polyfills';

import { inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { AuthService } from './auth.service';

import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from '../environments/environment';
import { appRoutes } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpRequestInterceptor } from './httpInterceptor';
import { WINDOW_PROVIDERS } from './shared/helpers/window.helper';
import { SharedModule } from './shared/shared.module';
import { ConfirmUserAccountComponent } from './views/confirm-user-account/confirm-user-account.component';
import { ContentListComponent } from './views/displayer/content-list/content-list.component';
import { CourseDisplayerComponent } from './views/displayer/courses/course-displayer/course-displayer.component';
import { CoursesListComponent } from './views/displayer/courses/courses-list/courses-list.component';
import { CoursesComponent } from './views/displayer/courses/courses.component';
import { GroupOverviewComponent } from './views/groups/group-overview/group-overview.component';

import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getRemoteConfig, provideRemoteConfig } from '@angular/fire/remote-config';
import { RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { MarkdownModule, MARKED_OPTIONS } from 'ngx-markdown';
import { QuillModule } from 'ngx-quill';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { ToastrModule } from 'ngx-toastr';
import { EnvServiceProvider } from './env.service.provider';
import { ContentModule } from './views/content/content.module';
import { NotesViewerComponent } from './views/displayer/notes/notes-viewer/notes-viewer.component';
import { SearchComponent } from './views/search/search.component';

import { getAuth, provideAuth } from '@angular/fire/auth';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import markedKatex from 'marked-katex-extension';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { AudioPlayerComponent } from './shared/components/audio-player/audio-player.component';
import { AuthenticationComponent } from './shared/components/authentication/authentication.component';
import { BlogCardComponent } from './shared/components/blog-card/blog-card.component';
import { ContentSearcherComponent } from './shared/components/content-searcher/content-searcher.component';
import { CourseCardComponent } from './shared/components/course-card/course-card.component';
import { CreateModeToggleComponent } from './shared/components/create-mode-toggle/create-mode-toggle.component';
import { CustomPaginatorComponent } from './shared/components/custom-paginator/custom-paginator.component';
import { DialogComponent } from './shared/components/dialog/dialog.component';
import { FloatEditMenuComponent } from './shared/components/float-edit-menu/float-edit-menu.component';
import { LanguageSelectorComponent } from './shared/components/language-selector/language-selector.component';
import { MoreButtonComponent } from './shared/components/more-button/more-button.component';
import { QuillToolbarComponent } from './shared/components/quill-toolbar/quill-toolbar.component';
import { AudioRecorderComponent } from './shared/components/recorders/audio-recorder/audio-recorder.component';
import { PhotoRecorderComponent } from './shared/components/recorders/photo-recorder/photo-recorder.component';
import { ScreenRecorderComponent } from './shared/components/recorders/screen-recorder/screen-recorder.component';
import { VideoRecorderComponent } from './shared/components/recorders/video-recorder/video-recorder.component';
import { ShareButtonComponent } from './shared/components/share-button/share-button.component';
import { ShareMenuDialogComponent } from './shared/components/share-menu-dialog/share-menu-dialog.component';
import { SidebarNavComponent } from './shared/components/sidebar-nav/sidebar-nav.component';
import { NotEnoughCreditComponent } from './shared/components/snackbar/not-enough-credit/not-enough-credit.component';
import { ContentDisplayCardComponent } from './shared/components/ui/content-display-card/content-display-card.component';
import { DrawerButtonComponent } from './shared/components/ui/drawer-button/drawer-button.component';
import { EmptyContentComponent } from './shared/components/ui/empty-content/empty-content.component';
import { FilterSectionWideComponent } from './shared/components/ui/filter-section-wide/filter-section-wide.component';
import { FilterToolbarComponent } from './shared/components/ui/filter-toolbar/filter-toolbar.component';
import { InteractiveListItemComponent } from './shared/components/ui/interactive-list-item/interactive-list-item.component';
import { LoadingIndicatorComponent } from './shared/components/ui/loading-indicator/loading-indicator.component';
import { NavListComponent } from './shared/components/ui/nav-list/nav-list.component';
import { SkeletonComponent } from './shared/components/ui/skeleton/skeleton.component';
import { TableWrapperComponent } from './shared/components/ui/table-wrapper/table-wrapper.component';
import { ChimeVideoCallDrawerComponent } from './shared/components/video-call-drawer-chime/chime-video-call-drawer/chime-video-call-drawer.component';
import { ContentShellComponent } from './shared/components/video-call-drawer-chime/in-content-video-call-shell/content-shell.component';
import { HasRoleDirective } from './shared/directives/has-role.directive';
import { MaterialModule } from './shared/material.module';
import { ExcerptPipe } from './shared/pipes/excerpt.pipe';
import { QuillIconInitializerService } from './shared/services/quill-icon-initializer.service';
import { ShareMenuComponent } from './shared/services/share-menu/share-menu.component';
import { TenantBrandingService } from './shared/services/tenant-branding/tenant-branding.service';
import { ToastComponent } from './shared/services/toast/toast.component';
import { GenerateContentPaneComponent } from './views/content/generate-content-pane/generate-content-pane.component';
import { KnowledgeNavigatorComponent } from './views/displayer/courses/knowledge-navigator/knowledge-navigator.component';
import { SearchBarComponent } from './views/displayer/courses/search-bar/search-bar.component';
import { PathListComponent } from './views/displayer/path/path-list/path-list.component';
import { PathViewerComponent } from './views/displayer/path/path-viewer/path-viewer.component';
import { CreateEventComponent } from './views/event/create-event/create-event.component';
import { EventModule } from './views/event/event.module';
import { QuestionViewerComponent } from './views/questions/question-viewer/question-viewer.component';
import { QuestionsComponent } from './views/questions/questions.component';
registerLocaleData(localeFr);

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const standalones = [
  SkeletonComponent,
  ExcerptPipe,
  FloatEditMenuComponent,
  ShareButtonComponent,
  BlogCardComponent,
  DialogComponent,
  NotEnoughCreditComponent,
  ContentSearcherComponent,
  CustomPaginatorComponent,
  AuthenticationComponent,
  CourseCardComponent,
  MoreButtonComponent,
  ShareMenuComponent,
  CreateEventComponent,
  ShareMenuDialogComponent,
  LanguageSelectorComponent,
  ChimeVideoCallDrawerComponent,
  ContentShellComponent,
  ToastComponent,
  DrawerButtonComponent,
  CreateModeToggleComponent,
  FilterSectionWideComponent,
  SidebarNavComponent,
  ContentDisplayCardComponent,
  EmptyContentComponent,
  KnowledgeNavigatorComponent,
  TableWrapperComponent,
  NavListComponent,
  InteractiveListItemComponent,
  QuillToolbarComponent,
  AudioPlayerComponent,
  AudioRecorderComponent,
  VideoRecorderComponent,
  ScreenRecorderComponent,
  PhotoRecorderComponent,
  GenerateContentPaneComponent,
  FilterToolbarComponent,
  LoadingIndicatorComponent,
  GroupOverviewComponent,
  QuestionsComponent,
  SearchComponent,
];
const customModules = [ContentModule, EventModule, SharedModule, MaterialModule];
const directives = [HasRoleDirective];
const firebaseProviders = [
  provideFirebaseApp(() => initializeApp(environment.googleCredentials)),
  provideAuth(() => getAuth()),
  provideFirestore(() => getFirestore()),
  provideStorage(() => getStorage()),
  provideFunctions(() => getFunctions()),
  provideMessaging(() => getMessaging()),
  provideRemoteConfig(() => getRemoteConfig()),
];

@NgModule({
  declarations: [
    AppComponent,
    MainLayoutComponent,
    ConfirmUserAccountComponent,

    CoursesComponent,
    CoursesListComponent,
    ContentListComponent,
    CourseDisplayerComponent,
    NotesViewerComponent,
    QuestionViewerComponent,
    PathListComponent,
    PathViewerComponent,
    SearchBarComponent,
  ],
  imports: [
    ...standalones,
    ...directives,
    ...customModules,
    CommonModule,
    HttpClientModule,
    FormsModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    ToastrModule.forRoot(),
    QuillModule.forRoot(),
    MonacoEditorModule.forRoot({
      baseUrl: '/assets/monaco/vs',
    }),
    MarkdownModule.forRoot({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: () => ({
          extensions: [
            markedKatex({
              throwOnError: false,
            }),
          ] as any,
        }),
      },
    }),
    RouterModule.forRoot(appRoutes, {
      initialNavigation: 'enabledBlocking',
      bindToComponentInputs: true,
      anchorScrolling: 'enabled',
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    ServiceWorkerModule.register('firebase-messaging-sw.js', {
      enabled: true,
    }),
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
  ],
  exports: [],
  providers: [
    ...firebaseProviders,
    EnvServiceProvider,
    WINDOW_PROVIDERS,
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestInterceptor,
      multi: true,
    },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    {
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: { fontSet: 'material-symbols-rounded' },
    },
    provideAppInitializer(() => inject(QuillIconInitializerService).initialize()),
    provideAppInitializer(() => inject(AuthService).init()()),
    provideAppInitializer(() => inject(TenantBrandingService).loadTenantConfig()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
